import { useQuery, useReactiveVar } from "@apollo/client";
import fellowLogo from "@static/img/fellow-logo.svg";
import githubLogo from "@static/img/github.png";
import hubspotLogo from "@static/img/hubspot.png";
import jiraLogo from "@static/img/jira-icon.png";
import linearLogo from "@static/img/linear.png";
import readAiLogo from "@static/img/read-ai-logo.svg";
import salesforceLogo from "@static/img/salesforce.png";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams-64.png";
import youtrackLogo from "@static/img/youtrack.svg";
import { useMemo } from "react";
import {
  GetOrganizationConnectedIntegrationTypesQuery,
  GetOrganizationConnectedIntegrationTypesQueryVariables,
  IntegrationTypes,
  LoggedInUserOrgFragment,
} from "types/graphql-schema";

import getOrganizationConnectedIntegrationTypesQuery from "@apps/programs/graphql/get-organization-connected-integration-types-query";
import { currentOrganizationVar } from "@cache/cache";
import { role } from "@helpers/constants";

export type Tool = {
  name: string;
  /** The same logo the home page's integrations list shows. */
  logo: string;
  /** The logo is the icon followed by the name, so only its left square shows. */
  logoIsWordmark?: boolean;
  /** Connected for the whole organization, from the flags it already carries. */
  fromOrganization?: (organization: LoggedInUserOrgFragment) => boolean;
  /** Connected when the organization has connected an integration of this type. */
  integrationType?: IntegrationTypes;
};

/**
 * The tools Generate Context can draw on, by the category the integrations page
 * files them under. Only connections the organization owns count: a token one
 * person added for themselves gives nobody else any evidence.
 */
export const readinessCategories: {
  label: string;
  what: string;
  tools: Tool[];
}[] = [
  {
    label: "Messaging",
    what: "Summaries of what each person discussed and decided",
    tools: [
      {
        name: "Slack",
        logo: slackLogo,
        fromOrganization: (organization) =>
          Boolean(organization.hasSlackConnection),
      },
      {
        name: "Teams",
        logo: teamsLogo,
        fromOrganization: (organization) =>
          Boolean(
            organization.hasTeamsInstallation ||
              organization.hasTeamsTranscriptConnection,
          ),
        integrationType: IntegrationTypes.Teams,
      },
    ],
  },
  {
    label: "Support + Engineering",
    what: "Tickets closed and pull requests shipped",
    tools: [
      {
        name: "GitHub",
        logo: githubLogo,
        fromOrganization: (organization) =>
          Boolean(organization.hasGithubInstallation),
      },
      {
        name: "Linear",
        logo: linearLogo,
        integrationType: IntegrationTypes.Linear,
      },
      { name: "Jira", logo: jiraLogo, integrationType: IntegrationTypes.Jira },
      {
        name: "YouTrack",
        logo: youtrackLogo,
        fromOrganization: (organization) =>
          Boolean(organization.hasYoutrackIntegration),
        integrationType: IntegrationTypes.Youtrack,
      },
    ],
  },
  {
    label: "CRM",
    what: "Deals and accounts each person worked",
    tools: [
      {
        name: "Salesforce",
        logo: salesforceLogo,
        integrationType: IntegrationTypes.Salesforce,
      },
      {
        name: "HubSpot",
        logo: hubspotLogo,
        integrationType: IntegrationTypes.Hubspot,
      },
    ],
  },
  {
    label: "Productivity",
    what: "Meeting transcripts and notes",
    tools: [
      {
        name: "Fellow",
        logo: fellowLogo,
        fromOrganization: (organization) =>
          Boolean(organization.hasFellowTranscriptSyncConnection),
      },
      {
        name: "Read AI",
        logo: readAiLogo,
        logoIsWordmark: true,
        integrationType: IntegrationTypes.ReadAi,
      },
    ],
  },
];

export const canManageIntegrations = (organization: LoggedInUserOrgFragment) =>
  [role.globalAdmin, role.peopleAdmin, role.itAdmin].includes(
    organization.userMembership?.role ?? "",
  );

export type ReadinessToolCategory = {
  label: string;
  what: string;
  tools: {
    name: string;
    logo: string;
    logoIsWordmark: boolean;
    connected: boolean;
  }[];
  /** True when any one of its tools is connected. */
  connected: boolean;
};

/**
 * Which tool categories the organization has connected, and whether the viewer
 * can connect more. Read by the tools list and by the savings figures, which
 * count only what is still to connect.
 */
export const useReadinessTools = (organizationId: number) => {
  const organization = useReactiveVar(currentOrganizationVar);
  // Only the types, not the integration rows: the rows are for admins only, and
  // a manager setting up a review must see the same status as an admin.
  const { data, loading } = useQuery<
    GetOrganizationConnectedIntegrationTypesQuery,
    GetOrganizationConnectedIntegrationTypesQueryVariables
  >(getOrganizationConnectedIntegrationTypesQuery, {
    variables: { organizationId },
  });

  // The same array until what is connected changes, so the savings figures
  // that depend on it are not worked out again on every render.
  const connectedTypes = data?.organization?.connectedIntegrationTypes;
  const categories = useMemo<ReadinessToolCategory[]>(() => {
    const integrationTypes = new Set(connectedTypes);
    const isConnected = (tool: Tool) =>
      Boolean(tool.fromOrganization?.(organization)) ||
      (tool.integrationType !== undefined &&
        integrationTypes.has(tool.integrationType));
    return readinessCategories.map((category) => {
      const tools = category.tools.map((tool) => ({
        name: tool.name,
        logo: tool.logo,
        logoIsWordmark: Boolean(tool.logoIsWordmark),
        connected: isConnected(tool),
      }));
      return {
        label: category.label,
        what: category.what,
        tools,
        connected: tools.some((tool) => tool.connected),
      };
    });
  }, [connectedTypes, organization]);
  return {
    categories,
    canManage: canManageIntegrations(organization),
    // Until the connected types arrive, only the organization's flags are
    // known, and a tool connected through an integration row reads as missing.
    loaded: !loading,
  };
};
